import { Button, Input, Popover, Tree } from "antd";
import React, { useEffect, useRef, useState } from "react";
import { FilterFilled } from "@ant-design/icons";
import { generateClassName, ObjTest, treeUtil } from 'jzfz-web-utils';
import './style/filterPopover.less';
import { TooltipPlacement } from "antd/es/tooltip";

const { getChildrenToList, getListFromTree, getParentNodes } = treeUtil;


interface TreeFilterPopoverProps {
    overlayClassName?: string;
    className?: string; // 图标className
    options: { key, parentKey?, title, children }[];  // 树结构数据
    value?: any[];
    searchFilter?: boolean;
    defaultExpandAll?: boolean;
    onChange?: (value: any) => void;
    onReset?: () => void;
    onConfirm?: () => void;
    onVisible?: (visible: boolean) => void; // 点击筛选图标时触发
    placement?: TooltipPlacement;
}


/**
 * 表头筛选菜单
 * @param props
 * @constructor
 */
const TreeFilterPopover = (props: TreeFilterPopoverProps) => {
    const {
        overlayClassName,
        className,
        options = [],
        value = [],
        onChange = (value: any) => { },
        onReset = () => { },
        onConfirm = () => { },
        onVisible = (visible: boolean) => { },
        searchFilter,
        defaultExpandAll,
        placement = 'bottom'
    } = props;
    const [visible, setVisible] = useState<boolean>(false);
    const [keywords, setKeywords] = useState<string>('');    // 搜索关键字
    const [treeData, setTreeData] = useState<any[]>([]);
    const [expandedKeys, setExpandedKeys] = useState<any[]>([]);    // 展开的key集合
    const treeDataCache = useRef<any[]>([]);    // 树结构数据缓存
    const treeRef = useRef<any>(null);  // 树组件引用
    const debounce = useRef<any>(null);
    const scrollDebounce = useRef<any>(null);


    useEffect(() => {
        if (!visible) {
            setKeywords('');
        }
    }, [visible])


    useEffect(() => {
        setTreeData(options);
        treeDataCache.current = getListFromTree(options);
        return () => {
            if (debounce.current) {
                clearTimeout(debounce.current);
            }
            if (scrollDebounce.current) {
                clearTimeout(scrollDebounce.current);
            }
        }
    }, [options])


    /**
     * 组件显示时，是否展开所有节点
     */
    useEffect(() => {
        if (visible && defaultExpandAll && ObjTest.isNotEmptyArray(options)) {
            const listTree = getListFromTree(options);
            const keys = listTree.map(r => r.key);
            setExpandedKeys(keys);
        }
        return () => {
            setExpandedKeys([]);
        }
    }, [visible, options])


    /**
     * 重置
     */
    const cancelHandle = () => {
        setVisible(false);
        onChange([]);
        onReset();
    }


    /**
     * 提交
     */
    const submitHandle = () => {
        setVisible(false);
        onConfirm();
    }


    /**
     * 延迟滚动到指定树节点
     * @param key
     */
    const scrollToTreeNode = (key: string) => {
        if (scrollDebounce.current) {
            clearTimeout(scrollDebounce.current);
        }
        if (!key) {
            return;
        }
        if (!treeRef.current) {
            return;
        }
        scrollDebounce.current = setTimeout(() => {
            treeRef.current.scrollTo({ key, align: 'top' });
        }, 50)
    }


    /**
     * 生成新的树
     * @param list
     * @param parentKey
     */
    const reGenerateTree = (list: any[], parentKey: string) => {
        let arr = [];
        list.forEach((v: any) => {
            if (v.parentKey === parentKey) {
                v.children = reGenerateTree(list, v.key);
                arr.push(v);
            }
        })
        return arr;
    }


    /**
     * 查找选中的节点集
     * @param keys
     * @returns
     */
    const searchNodes = (keys: string[]): any[] => {
        let nodes = [];
        keys.forEach((key: string) => {
            let parentNodes = getParentNodes(key, treeDataCache.current);
            nodes = [...nodes, ...parentNodes];
        })
        // 去重
        let newKeys = Array.from(new Set([...nodes.map((node: any) => node.key), ...keys]));
        const newNodes = newKeys.map((key: string) => {
            let target = treeDataCache.current.find((node: any) => node.key === key);
            return target ? { ...target } : null;
        })
        return reGenerateTree(newNodes.filter((v: any) => v), undefined);
    }


    /**
     * 搜索
     * @param e
     */
    const inputChangeHandle = (e: any) => {
        const { value } = e.target;
        setKeywords(value);
        if (debounce.current) {
            clearTimeout(debounce.current);
        }
        // 输入值为空时：不选中且显示原始列表
        if (!value || !value.trim()) {
            setTreeData(options);
            onChange([]);
            // 若配置展开，则全部展开
            if (defaultExpandAll) {
                const keys = treeDataCache.current.map(r => r.key);
                setExpandedKeys(keys);
            }
            return;
        }
        debounce.current = setTimeout(() => {
            // 寻找匹配上关键字的节点的key
            let keys = treeDataCache.current.filter((v: any) => v.title && v.title.includes(value)).map((v: any) => v.key);
            // 寻找所有匹配上关键字的节点的子节点的key
            let allSelectedKeys = [...keys];
            keys.forEach((v: any) => {
                let childKeys = getChildrenToList(treeDataCache.current, v, { key: 'key', children: 'children' }).map((v: any) => v.key);
                allSelectedKeys = [...allSelectedKeys, ...childKeys];
            })
            allSelectedKeys = Array.from(new Set(allSelectedKeys));
            // 设置选中和勾选
            onChange(allSelectedKeys);
            if (searchFilter) {
                setTreeData(searchNodes(allSelectedKeys));
            } else {
                // 设置展开节点key
                let parentKeys = [];
                keys.forEach((key: any) => {
                    let tempKeys = getParentNodes(key, treeDataCache.current, { key: 'key', children: 'children' })
                        .map((v: any) => v.key);
                    parentKeys = [...parentKeys, ...tempKeys];
                })
                parentKeys = Array.from(new Set(parentKeys));
                setExpandedKeys(parentKeys);
                scrollToTreeNode(keys[0]);
            }
        }, 600)
    }


    return (
        <Popover
            overlayClassName={generateClassName(['custom-table-filter-popover-wrap', overlayClassName])}
            trigger="click"
            visible={visible}
            onVisibleChange={(visible: boolean) => {
                setVisible(visible);
                onVisible(visible);
            }}
            placement={placement}
            zIndex={100}
            content={() => {
                return (
                    <div>
                        <div>
                            <Input
                                placeholder="请输入搜索关键字筛选"
                                allowClear
                                value={keywords}
                                onChange={inputChangeHandle}
                            />
                        </div>
                        <Tree
                            ref={treeRef}
                            checkable
                            treeData={treeData}
                            height={300}
                            multiple={true}
                            checkedKeys={value}
                            expandedKeys={expandedKeys}
                            onExpand={(keys: string[]) => {
                                setExpandedKeys(keys);
                            }}
                            onCheck={(keys: string[]) => {
                                onChange(keys);
                            }}
                        />
                        <div className="flexic mt10">
                            <Button
                                className="f12"
                                size="small"
                                onClick={cancelHandle}>重置
                            </Button>
                            <Button
                                className="mla f12 submit-btn"
                                size="small"
                                onClick={submitHandle}>确定
                            </Button>
                        </div>
                    </div>
                )
            }}
        >
            <FilterFilled
                className={
                    generateClassName([
                        'custom-table-filter-popover-icon',
                        ObjTest.isNotEmptyArray(value) ? 'active' : '',
                        className,
                    ])
                }
            />
        </Popover>
    )
}


export default TreeFilterPopover;
